import React, { useEffect, useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { api, unwrap } from "@/lib/api";

export default function TermsPage() {
  const [phone, setPhone] = useState("508-397-0721");
  useEffect(() => {
    api.get("/public/settings").then((r) => {
      const d = unwrap(r) || {};
      setPhone(d.company_phone || d.support_phone || "508-397-0721");
    }).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="terms-page">
      <SEO
        title="Terms of Service — Ever Clean Home"
        description="The rules of the road for booking residential cleaning with Ever Clean Home — payment, cancellations, liability, intellectual property, dispute resolution and more."
      />
      <Header />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 prose prose-stone">
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">Terms of Service</h1>
        <p className="text-sm text-[#4B5563]">Last updated: {new Date().toLocaleDateString()}</p>

        <p>
          These Terms of Service ("Terms") govern your use of <strong>Ever Clean Home</strong>'s ("we", "us", "our") website
          and residential cleaning services. By booking a service or using our website you agree to be bound by these
          Terms. If you do not agree, please do not use our website or services. This document is a good-faith plain-language
          draft and is <em>not</em> a substitute for legal advice — please have it reviewed by an attorney licensed in
          Massachusetts before relying on it.
        </p>

        <h3>1. The service</h3>
        <p>
          Ever Clean Home provides residential cleaning services in Boston (MA), Cambridge, Brookline, Somerville,
          Providence (RI), Hartford (CT) and other surrounding areas listed on our website. The exact scope of a job is
          defined by the inputs you submit through the booking wizard (property type, size, rooms, selected extras) and
          any additional notes. Anything outside of that scope may incur additional charges, billed transparently before
          extra work begins.
        </p>

        <h3>2. Booking and quotes</h3>
        <p>
          Our website displays a live quote based on your inputs. The quote becomes a confirmed price the moment you
          complete payment via Stripe. We reserve the right to re-quote a job (or cancel and refund) if upon arrival the
          conditions materially differ from what you submitted (e.g., size, hazardous conditions, biohazards, hoarding).
          In such cases we will contact you before proceeding.
        </p>

        <h3>3. Payment</h3>
        <p>
          All payments are processed by Stripe, Inc. on its hosted, PCI-DSS-compliant checkout page. We do not store
          full card numbers. By providing a payment method you authorise Ever Clean Home to charge the agreed total at
          the time of booking. Sales tax (if applicable in your state) is added at checkout.
        </p>

        <h3>4. Cancellation, rescheduling and refunds</h3>
        <ul>
          <li><strong>More than 24h before service:</strong> free cancellation, full refund.</li>
          <li><strong>Within 24h of service:</strong> a partial cancellation fee may apply (typically 25% of the booking total) to cover scheduling losses.</li>
          <li><strong>No-show (cleaner can't access the property):</strong> a no-show fee equal to 50% of the booking may be charged.</li>
          <li><strong>Rescheduling:</strong> free with at least 24h notice. Within 24h, on a best-effort basis.</li>
          <li><strong>Refunds:</strong> processed through Stripe back to the original payment method within 5–10 business days.</li>
        </ul>

        <h3>5. Satisfaction guarantee</h3>
        <p>
          We're proud of our work. If something's not right, contact us within 24 hours of the service via email or
          WhatsApp and we will return to re-clean the area at no additional cost. The guarantee covers the original
          scope of work only.
        </p>

        <h3>6. Your responsibilities</h3>
        <p>By booking, you confirm that you have the legal right to allow our team into the property to be cleaned, you will provide accurate access instructions, and you will secure pets, valuables and fragile items prior to the visit.</p>

        <h3>7. Liability</h3>
        <p>
          Ever Clean Home is fully insured and bonded. Despite reasonable care, accidents may happen. We accept
          responsibility for actual damage caused by our team's negligence during a cleaning visit, provided you notify
          us in writing within 24 hours of the visit and reasonably document the issue (photos). We are <strong>not</strong>{" "}
          responsible for: pre-existing damage; wear and tear; items not properly secured; loss of value of antiques or
          irreplaceable items unless declared in advance; or indirect, consequential, special or punitive damages. To the
          maximum extent permitted by law, our total liability under these Terms is capped at the amount you paid for the
          service that gave rise to the claim.
        </p>

        <h3>8. Intellectual property</h3>
        <p>
          All content on our website — text, images, logos, branding, code, design — is owned by Ever Clean Home or its
          licensors and is protected by U.S. and international copyright, trademark and other intellectual property
          laws. You may not copy, modify, distribute or create derivative works of our content without our prior written
          permission, except as strictly necessary for personal, non-commercial use.
        </p>

        <h3>9. Modifications to the service or Terms</h3>
        <p>
          We may update our service, pricing, coverage areas and these Terms at any time. Changes take effect when posted
          to this page. The "Last updated" date at the top reflects the latest revision. Continued use of our website or
          services after a change indicates acceptance. For material changes affecting existing bookings, we will email
          impacted customers.
        </p>

        <h3>10. Governing law and dispute resolution</h3>
        <p>
          These Terms are governed by the laws of the <strong>Commonwealth of Massachusetts</strong>, without regard to
          conflict-of-law principles. Any disputes that cannot be resolved informally within 60 days shall be submitted to{" "}
          <strong>binding arbitration</strong> administered by the American Arbitration Association under its Consumer
          Arbitration Rules, in Suffolk County, Massachusetts, with one arbitrator. Either party may seek emergency
          equitable relief in a Massachusetts court. <em>Class actions are waived</em> to the extent permitted by law.
          Notwithstanding the foregoing, small-claims court remains an available forum for qualifying claims.
        </p>

        <h3>11. Force majeure</h3>
        <p>
          Neither party is liable for delay or failure to perform due to events beyond reasonable control, including
          severe weather, natural disasters, epidemics, government action, civil unrest, utility outages, strikes,
          terrorism or other acts of force majeure. We will make commercially reasonable efforts to reschedule affected
          appointments.
        </p>

        <h3>12. Indemnification</h3>
        <p>
          You agree to indemnify and hold Ever Clean Home harmless from any claim, loss, damage, liability or expense
          (including reasonable attorneys' fees) arising out of your breach of these Terms, your misrepresentation, your
          violation of any law, or your infringement of any third-party right.
        </p>

        <h3>13. Termination</h3>
        <p>
          We may suspend or terminate your access to our services for any breach of these Terms, suspected fraud, abusive
          behaviour toward our team, or failure to pay. Any sums owed at the time of termination remain due.
        </p>

        <h3>14. Severability and entire agreement</h3>
        <p>
          If any provision of these Terms is found unenforceable, the remaining provisions stay in effect. These Terms,
          together with our Privacy Policy, are the entire agreement between you and Ever Clean Home regarding the use
          of our website and services and supersede any prior agreements.
        </p>

        <h3>15. Contact</h3>
        <p data-testid="terms-contact-phone">Questions about these Terms? Email <strong>bookings@everclean.com</strong> or call <strong>{phone}</strong>.</p>
        <p>For details about text message notifications, see our <a href="/sms">SMS Program</a> page and the
          <a href="/privacy#sms"> SMS section</a> of our Privacy Policy.</p>

        <p className="text-xs text-[#9CA3AF] mt-10">
          <em>This document is a good-faith draft and is not legal advice. Please have it reviewed by an attorney licensed in your jurisdiction before relying on it.</em>
        </p>
      </div>
      <Footer />
    </div>
  );
}
